<script lang="ts">
	import Layout from "../components/Layout.svelte";
	import { getCorrectCartInStepOrRedirect } from "../components/Cart/getCorrectCartInStepOrRedirect.js";
	import { CartStep, cartStepPathMap, getNextStep } from "../stores/cart/CartStep.js";
	import { getGlobalSettings } from "../getGlobalSettings.js";
	import Button from "../components/Button.svelte";
	import { formatPrice } from "../../../core/utils/formatPrice.js";
	import CartProduct from "../components/Cart/CartProduct.svelte";
	import CartSteps from "../components/Cart/CartSteps.svelte";
	import { createPromiseStore } from "../../../core/utils/createPromiseStore.js";
	import { getFormFields } from "../utils/getFormFields.js";
	import type { CartOverviewProps } from "./CartOverviewProps.js";
	import type { CartFieldsForStep } from "../stores/cart/Cart.js";
	import { getPriceOfOrder } from "../../../core/schema/utils/getPriceOfOrder.js";
	import { PriceLevel } from "../../../core/schema/PriceLevel.js";
	import type { DeepPartial } from "../../../core/utils/DeepPartial.js";
	import { trySaveCartFields } from "../components/Cart/trySaveCartFields.js";
	import SavedForm from "../components/Form/SavedForm.svelte";
	import { initSentry } from "../utils/initSentry.js";
	import { onMount } from "svelte";
	import basket from "../../../core/assets/icons/basket.svg?raw";
	import Icon from "../components/Icon.svelte";
	import { nbspify } from "../../../core/utils/nbspify.js";
	import CartSummary from "../components/Cart/CartSummary.svelte";
	import { reportCheckoutBegan } from "../UniversalAnalytics";
	import Preloader from "../components/Preloader.svelte";
	import Link from "../components/Link.svelte";
	import cake from "../../../core/assets/icons/cake.svg?raw";
	import chevronLeft from "../../../core/assets/icons/arrow-left.svg?raw";
	import { categoryPathMap } from "../../../core/schema/paths/categoryPathMap.js";
	import { writable } from "svelte/store";
	import { inViewport } from "../utils/inViewport.js";
	import CartVoucher from "../components/Cart/CartVoucher.svelte";
	import LabelledInput from "../components/Form/LabelledInput.svelte";
	import { getVoucherDiscount } from "../../../core/schema/utils/getVoucherDiscount";
	import { getClients } from "../appsync/getClients";
	import getVoucherByCode from "../appsync/queries/getVoucherByCode.graphql";
	import type { Voucher } from "../../../core/schema/Voucher";
	import { getCartStore } from "../contexts/cartStoreContextKey.js";
	import { isCartEmpty } from "../stores/cart/isCartEmpty.js";
	import Message from "../components/Message.svelte";
	import { deliveryTypeToBranchMap } from "../../../core/schema/deliveryTypeToBranchMap.js";
	import CrossSale from "../components/CrossSale.svelte";

	type $$Props = CartOverviewProps;

	const cartStore = getCartStore();
	const { crossSaleProducts, maxProductsPerCategory, orderStops, scheduledOrderStops } = $$restProps as $$Props;

	const globalSettings = getGlobalSettings();
	const { TENANT_LOCALE } = globalSettings;

	const currentStep = CartStep.Overview;
	const nextStep = getNextStep(currentStep);
	type FieldsForStep = CartFieldsForStep<typeof currentStep>;
	$: ({ cartInStep } = getCorrectCartInStepOrRedirect(
		cartStore,
		TENANT_LOCALE,
		currentStep,
		$cartStore.value,
		orderStops,
		scheduledOrderStops,
		maxProductsPerCategory,
	));

	let voucherCode = "";
	let voucherCodeErr = "";
	$: price = getPriceOfOrder(
		cartInStep.order,
		PriceLevel.Basic,
		cartInStep.order.deliveryType !== undefined
			? (deliveryTypeToBranchMap[cartInStep.order.deliveryType] ?? null)
			: null,
	);
	$: appliedVoucher = cartInStep.order.appliedVoucher ?? undefined;
	$: voucherCode = cartInStep.usedVoucherCode ?? voucherCode;

	onMount(async () => {
		await initSentry(globalSettings);
		reportCheckoutBegan(cartInStep.order, TENANT_LOCALE);
	});

	const advance = createPromiseStore(
		(input: FieldsForStep): Promise<boolean> => cartStore.advanceToNextStep(currentStep, input),
	);

	function validateFields(fields: DeepPartial<FieldsForStep>): fields is FieldsForStep {
		return [].every(Boolean);
	}

	function getFields(form: HTMLFormElement, validate: true): FieldsForStep;
	function getFields(form: HTMLFormElement, validate: false): DeepPartial<FieldsForStep>;
	function getFields(form: HTMLFormElement, validate: boolean): FieldsForStep | DeepPartial<FieldsForStep> {
		const { note } = getFormFields(form);
		const fields: DeepPartial<FieldsForStep> = { note: (note ?? "").trim() || undefined };
		if (validate && !validateFields(fields)) {
			throw new Error("Fields are not valid");
		}
		return fields;
	}

	async function saveFields(form: HTMLFormElement): Promise<boolean> {
		return await trySaveCartFields(cartStore, form, currentStep, cartInStep, getFields(form, false));
	}

	async function submit(form: HTMLFormElement): Promise<void> {
		const fields = getFields(form, true);
		const canAdvance = await advance.call(fields);
		if (!canAdvance) {
			return;
		}

		window.location.assign(`/${cartStepPathMap[nextStep][TENANT_LOCALE]}`);
	}

	function changeValue(event: Event): void {
		const target = event.target as HTMLInputElement;
		voucherCodeErr = "";
		voucherCode = target.value;
	}

	const getVoucher = createPromiseStore(async (code: Voucher["code"]) => {
		const { graphQL } = await getClients(globalSettings);
		return graphQL.query<Voucher, { code: Voucher["code"] }>({
			query: getVoucherByCode,
			variables: {
				code,
			},
		});
	});

	async function applyVoucher(): Promise<void> {
		voucherCodeErr = "";
		const voucher = await getVoucher.call(voucherCode);

		if (!voucher) {
			voucherCodeErr = "Tento kód není platný";
			return;
		}

		const { id, value } = voucher;

		await cartStore.applyVoucher(
			{
				id,
				value,
			},
			voucherCode,
		);
	}

	$: hasItemsInCart = !isCartEmpty($cartStore.value);

	const continueInViewport = writable(false);
</script>

<Layout hasPanel={!$continueInViewport && hasItemsInCart} hideFooter>
	<section class="container">
		<h1>{nbspify("Obsah vaší krabice")}</h1>

		<SavedForm {saveFields} {submit} class="flex flex-col gap-8">
			{#if hasItemsInCart}
				<CartSteps {currentStep} {nextStep} />
			{/if}

			{#if !$cartStore.loaded}
				<Preloader />
			{:else}
				<div class="flex flex-col">
					{#if hasItemsInCart || appliedVoucher}
						{#each cartInStep.order.products as orderProduct, index (orderProduct.id)}
							<CartProduct {orderProduct} {index} {maxProductsPerCategory} />
						{/each}
						{#each cartInStep.order.vouchers as orderVoucher, index (orderVoucher.id)}
							<CartVoucher {orderVoucher} {index} />
						{/each}
					{:else}
						<div class="mx-auto mb-24 mt-16 flex flex-col gap-2 font-medium">
							<Icon icon={basket} class="text-secondary mx-auto w-16" />
							<p class="text-md text-center md:text-lg">Krabice je prázdná</p>
						</div>
					{/if}
				</div>

				{#if hasItemsInCart}
					{#if $cartStore.value.order.vouchers.length > 0}
						<div class="flex flex-col">
							<Message variant="info">
								Váš kód voucheru vám přijde na e-mail po zaplacení.
								<br />
								Pokud však chcete vystavit voucher do obálky, kdykoli se stavte na některou z poboček, nahlašte obsluze číslo
								voucheru a bude vám vystaven fyzický dárkový poukaz.
							</Message>
						</div>
					{/if}

					<div class="flex w-full flex-col gap-8 pb-8">
						<div class="flex flex-col justify-between gap-8 sm:flex-row">
							<div class="flex flex-col gap-1">
								<div class="flex flex-wrap items-start gap-3 sm:flex-nowrap">
									<LabelledInput
										id="voucherCode"
										on:input={(event) => {
											changeValue(event);
										}}
										disabled={appliedVoucher !== undefined}
										showValid={appliedVoucher !== undefined}
										bind:value={voucherCode}
										showErrorMessage={Boolean(voucherCodeErr)}
										errorMessage={voucherCodeErr}
									>
										Kód poukazu
									</LabelledInput>
									{#if voucherCode && !appliedVoucher}
										<Button
											class="my-3 justify-center lg:rounded-full"
											type="button"
											disabled={$advance.status === "loading" || $getVoucher.status === "loading"}
											on:click={applyVoucher}
										>
											Přidat
										</Button>
									{/if}
									{#if appliedVoucher}
										<Button
											class="my-3 justify-center lg:rounded-full"
											type="button"
											variant="secondary"
											disabled={$advance.status === "loading" || $getVoucher.status === "loading"}
											on:click={async () => {
												await cartStore.unapplyVoucher();
											}}
										>
											Odebrat
										</Button>
									{/if}
								</div>
							</div>

							<div
								class="flex flex-col items-end"
								use:inViewport={{
									store: continueInViewport,
								}}
							>
								{#if appliedVoucher}
									<div
										class="border-b-1 grid w-full grid-cols-[auto,auto] grid-rows-2 items-center gap-x-8 md:gap-x-[4.5rem]"
									>
										<p class="text-xs lg:text-base">Celkem bez DPH</p>
										<p class="text-md text-right font-bold lg:text-lg">
											{nbspify(formatPrice(price.totalPrice.withoutTax, TENANT_LOCALE, true))}
										</p>
										<p class="text-xs lg:text-base">Celkem s DPH</p>
										<p class="text-md text-right font-bold lg:text-lg">
											{nbspify(formatPrice(price.totalPrice.withTax, TENANT_LOCALE, true))}
										</p>
									</div>
									<div
										class="border-b-1 grid w-full grid-cols-[auto,auto] grid-rows-1 items-center gap-x-8 md:w-full md:gap-x-[4.5rem]"
									>
										<p class="text-xs lg:text-base">Uplatněný poukaz</p>
										<p class="text-md text-right font-bold lg:text-lg">
											{nbspify(
												formatPrice(
													-getVoucherDiscount(appliedVoucher.value, price.totalPrice.withTax),
													TENANT_LOCALE,
													true,
												),
											)}
										</p>
									</div>
									<div
										class="grid w-full grid-cols-[auto,auto] grid-rows-1 items-center gap-x-8 md:w-full md:gap-x-[4.5rem]"
									>
										<p class="text-xs lg:text-base">K úhradě s DPH</p>
										<p class="text-md text-right font-bold lg:text-lg">
											{nbspify(
												formatPrice(
													Math.max(
														price.totalPrice.withTax -
															getVoucherDiscount(appliedVoucher.value, price.totalPrice.withTax),
														0,
													),
													TENANT_LOCALE,
													true,
												),
											)}
										</p>
									</div>
								{:else}
									<div class="grid w-fit grid-cols-[auto,auto] grid-rows-2 items-center gap-x-8 md:gap-x-[4.5rem]">
										<p class="text-xs lg:text-base">Celkem bez DPH</p>
										<p class="text-md text-right font-bold lg:text-lg">
											{nbspify(formatPrice(price.totalPrice.withoutTax, TENANT_LOCALE, true))}
										</p>
										<p class="text-xs lg:text-base">K úhradě s DPH</p>
										<p class="text-md text-right font-bold lg:text-lg">
											{nbspify(formatPrice(price.totalPrice.withTax, TENANT_LOCALE, true))}
										</p>
									</div>
								{/if}
							</div>
						</div>
						<div class="flex flex-wrap justify-end gap-4 sm:justify-between">
							<Link
								class="lg:rounded-full lg:px-6 lg:py-[.65rem]"
								asButton
								icon={chevronLeft}
								iconClass="h-4"
								iconFront
								variant="secondary"
								href="/{categoryPathMap.cakes[TENANT_LOCALE]}"
							>
								Pokračovat v nákupu
							</Link>

							<Button
								class="justify-center lg:rounded-full lg:px-10 lg:py-[.65rem]"
								type="submit"
								disabled={$advance.status === "loading" || $getVoucher.status === "loading"}
								icon={cake}
								iconClass="h-5"
							>
								Pokračovat v objednávce
							</Button>
						</div>
					</div>
				{/if}

				<CartSummary
					isButtonDisabled={$advance.status === "loading" || $getVoucher.status === "loading"}
					hide={$continueInViewport}
				/>
			{/if}
		</SavedForm>
	</section>

	{#if $cartStore.loaded}
		<section class="container">
			<CrossSale {crossSaleProducts} loadedCartStore={cartStore} {maxProductsPerCategory} />
		</section>
	{/if}
</Layout>
